import React from "react";
import { createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Body, Label } from "../../../../components/Typography";
import { OrderDetailCard, CardSection } from "./OrderDetailCard";
import { PaymentLink } from "./PaymentLink";
import { PricingLineItem } from "./PricingLineItem";
import {
  flexColumn,
  formatOrderDate,
  getCurrencyLabel,
  getPaymentStatusDisplayName,
  isOrderPaid,
  type Order,
  type OrderPricing,
} from "./shared";

const useStyles = createStyles(() => ({
  lineItems: {
    ...flexColumn,
    gap: tokens.spacing[1],
    paddingLeft: tokens.spacing[4],
  },
}));

export const PricingBreakdown: React.FC<{
  pricing: OrderPricing;
  label: string;
  prefix?: string;
}> = ({ pricing, label, prefix }) => {
  const { classes } = useStyles();
  return (
    <CardSection>
      <Label>
        {label} ({getCurrencyLabel(pricing.currency)})
      </Label>
      <div className={classes.lineItems}>
        <PricingLineItem label="Items" currency={pricing.currency} amount={pricing.itemsTotal} prefix={prefix} />
        <PricingLineItem label="Shipping" currency={pricing.currency} amount={pricing.shippingTotal} prefix={prefix} />
        <PricingLineItem
          label="Taxes"
          currency={pricing.currency}
          amount={pricing.taxesTotal}
          border="solid"
          prefix={prefix}
        />
        <PricingLineItem
          label="Order Total"
          currency={pricing.currency}
          amount={pricing.orderTotal}
          border="none"
          prefix={prefix}
        />
      </div>
    </CardSection>
  );
};

const SANDBOX_PAYMENT_MESSAGE = "There are no payments for test orders in the sandbox.";

export const PaymentCard: React.FC<{ order: Order; isSandbox: boolean }> = ({ order, isSandbox }) => {
  const statusName = getPaymentStatusDisplayName(order.paymentStatus);
  const paid = isOrderPaid(order);

  if (isSandbox) {
    return (
      <OrderDetailCard>
        <CardSection>
          <Label>Payment Status</Label>
          <Body>{SANDBOX_PAYMENT_MESSAGE}</Body>
        </CardSection>
      </OrderDetailCard>
    );
  }

  return (
    <OrderDetailCard>
      <CardSection>
        <Label>Payment Status</Label>
        <Body>
          {statusName}
          {paid && order.salePayment?.createdAt && <> on {formatOrderDate(order.salePayment.createdAt)}</>}
          {paid && order.salePayment?.id != null && <PaymentLink paymentId={order.salePayment.id} />}
        </Body>
      </CardSection>

      {paid && order.pricing && <PricingBreakdown pricing={order.pricing} label="Total Order Amount" />}
    </OrderDetailCard>
  );
};
